import { Component, Injector, ChangeDetectorRef, OnDestroy, ElementRef, OnInit } from '@angular/core';

import { BaseFormComponent } from '../base-form.component';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { CoverpathControllerInterface } from '../../../../interfaces/controller/CoverpathControllerInterface';
import { ValidationService } from '../../../../services/validation/validation.service';
import type { Policy } from '../../../../../../common/js/customer/Policy';
import { ValidationUtils } from '../../../../../../coverpath-common/js/static/ValidationUtils';

@Component({
  moduleId: module.id,
  selector: 'section-please-acknowledge',
  templateUrl: './section-please-acknowledge.component.html',
})
export class SectionPleaseAcknowledgeComponent extends BaseFormComponent {
  applicationFormName: string;
  helpText: string = 'Your client must answer this question.';
  insuredName: string = '';
  ownerName: string = '';
  otherOwnerName: string = '';
  showOwnerTooltip: boolean = false;
  showInsuredTooltip: boolean = false;
  insuredHasAcknowledged: boolean = false;
  ownerHasAcknowledged: boolean = false;
  elementRef: ElementRef;
  controller: CoverpathControllerInterface;
  contractState: string = '';
  isMMD: boolean = false;
  otherOwnerHasAcknowledged: boolean = false;
  isLifeBridge: boolean = false;
  requiresSimpleInsuredAcknowledgement: boolean = false;
  requiresOwnerAcknowledgement: boolean = false;
  requiresJointOwnerAcknowledgement: boolean = false;
  policy: Policy;

  constructor(protected injector: Injector, private amorphicService: AmorphicService) {
    super(injector);
    this.controller = this.amorphicService.controller;
    this.policy = this.controller.customer.application.primaryPolicy;
  }

  async ngOnInit() {
    this.updateFromModel();

    const policy = this.controller.customer.application.primaryPolicy;
    this.contractState = policy.getApplicationState();
    this.isMMD = !!policy.isMMDirectPolicy();

    const insuredFirstName = policy.insured.person.firstName;
    const insuredLastName = policy.insured.person.lastName;
    this.insuredName = `${insuredFirstName} ${insuredLastName}`;
    // for juvenile
    if (policy.insuredIsJuvenile()) {
      const signerFullName = policy.insured.signer.fullName;
      this.insuredName = `${signerFullName} on behalf of ${this.insuredName}`;
    }

    this.ownerName = `${policy.owner.getFullNameOfOwner()}`;
    if (policy.owner.getIsEntityOrTrust()) {
      const entitySignerName = policy.ownerEntity.authorizedSigner.getFullNameOfOwner();
      this.ownerName = `${entitySignerName} on behalf of ${this.ownerName}`;
    }

    this.otherOwnerName = policy.otherOwner && policy.otherOwner.getFullName();
    this.isLifeBridge = policy.isLifeBridge();
    if (this.isLifeBridge) {
      // Mass Mutual owns all Life Bridge policies, so no owner acknowledgement is required
      this.ownerHasAcknowledged = true;
      await this.triggerAcknowledgeOwner();
    }

    const { earlyInsuredConsentEnabled } = this.controller;
    this.requiresSimpleInsuredAcknowledgement = ValidationUtils.requiresSimpleInsuredAcknowledgement(
      policy,
      earlyInsuredConsentEnabled
    );
    this.requiresOwnerAcknowledgement = ValidationService.requiresOwnerAcknowledgement(policy);
    this.requiresJointOwnerAcknowledgement = ValidationService.requiresJointOwnerAcknowledgement(policy);
  }

  updateFromModel(): void {
    this.ownerHasAcknowledged = this.getValue('customer.application.ownerHasAcknowledged');
    this.insuredHasAcknowledged = this.getValue('customer.application.insuredHasAcknowledged');
    this.otherOwnerHasAcknowledged = this.getValue('customer.application.otherOwnerHasAcknowledged');
  }

  resetValue(): void {}

  viewTooltip(insured: boolean): void {
    insured ? (this.showInsuredTooltip = true) : (this.showOwnerTooltip = true);
  }

  hideTooltip(insured: boolean): void {
    insured ? (this.showInsuredTooltip = false) : (this.showOwnerTooltip = false);
  }

  toggleTooltip(insured: boolean): void {
    insured ? (this.showInsuredTooltip = !this.showInsuredTooltip) : (this.showOwnerTooltip = !this.showOwnerTooltip);
  }

  async triggerAcknowledgeOwner() {
    await this.validateAndSet('customer.application.ownerHasAcknowledged', this.ownerHasAcknowledged);
  }

  async triggerAcknowledgeInsured() {
    await this.validateAndSet('customer.application.insuredHasAcknowledged', this.insuredHasAcknowledged);
  }

  async triggerAcknowledgeOtherOwner() {
    await this.validateAndSet('customer.application.otherOwnerHasAcknowledged', this.otherOwnerHasAcknowledged);
  }

  handleClick(event): void {
    let clickedComponent = event.target;
    let inside = false;
    do {
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);
    if (!inside) {
      this.showInsuredTooltip = false;
      this.showOwnerTooltip = false;
    }
  }
}
